import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BrainCircuit, ExternalLink, X } from 'lucide-react';
import type { plans } from '@/lib/client';
import { cn } from '@/lib/utils';
import { TRACE_POLL_MS, useTraceStore, type TraceSelection } from '@/stores/traceStore';
import { PipelineDiagram } from './PipelineDiagram';

/** The pipeline's stages, in order (the experiment UI's strip). */
const STAGES: Array<{ id: plans.TraceStage; label: string }> = [
  { id: 'read', label: 'Read' },
  { id: 'ask', label: 'Ask' },
  { id: 'plan', label: 'Plan' },
  { id: 'run', label: 'Run' },
  { id: 'revert', label: 'Revert' },
];

const STAGE_TONE: Record<plans.TraceStage, string> = {
  read: 'text-sky-300',
  ask: 'text-amber-300',
  plan: 'text-violet-300',
  run: 'text-emerald-300',
  revert: 'text-orange-300',
};

const linkOf = (kind: plans.TraceKind, id: string | null): string | null => (id === null ? null : kind === 'document' ? `/changes/documents/${id}` : `/changes/${id}`);

const timeOf = (at: string): string => new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** Where one item is: the strip, its own stage lit. */
function Strip({ stage }: { stage: plans.TraceStage }) {
  const at = STAGES.findIndex((s) => s.id === stage);
  return (
    <div className="mt-1.5 flex items-center gap-1 text-[10px] uppercase tracking-wide">
      {STAGES.map((s, i) => (
        <span key={s.id} className={cn('rounded px-1.5 py-0.5', i === at ? `bg-secondary ${STAGE_TONE[s.id]}` : i < at ? 'text-muted-foreground' : 'text-muted-foreground/40')}>
          {s.label}
        </span>
      ))}
    </div>
  );
}

/**
 * ZAC's reasoning, from any page (docs/plans/document-to-plan-pipeline.md, R4): what is under way and where it is in
 * the pipeline, then the latest lines, newest first. Informative only.
 */
export function ReasoningPanel() {
  const { open, active, entries, selected, close, read, select } = useTraceStore();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open) return;
    void read();
    const timer = setInterval(() => void read(), TRACE_POLL_MS);
    return () => clearInterval(timer);
  }, [open, read]);

  // A new page draws its own item: a choice made on the last page is let go.
  useEffect(() => select(null), [pathname, select]);

  if (!open) return null;
  // Drawn: the one chosen, else the page's own document or plan, else the first under way, else the latest the log names.
  const latest = entries.find((e) => e.ref_id !== null);
  const doc = /^\/changes\/documents\/([0-9a-f-]{36})/.exec(pathname)?.[1];
  const plan = doc ? undefined : /^\/changes\/([0-9a-f-]{36})/.exec(pathname)?.[1];
  const page: TraceSelection | null = doc ? { kind: 'document', id: doc, title: 'this document' } : plan ? { kind: 'plan', id: plan, title: 'this plan' } : null;
  const drawn: TraceSelection | null = selected
    ?? page
    ?? (active[0] ? { kind: active[0].kind, id: active[0].id, title: active[0].title } : null)
    ?? (latest ? { kind: latest.kind, id: latest.ref_id!, title: latest.title } : null);

  const Name = ({ s }: { s: TraceSelection }) => {
    const to = linkOf(s.kind, s.id);
    return (
      <span className="inline-flex min-w-0 items-center gap-1">
        <button onClick={() => select(s)} className={cn('truncate text-left hover:underline', drawn?.id === s.id ? 'text-foreground' : 'text-muted-foreground')}>{s.title || s.kind}</button>
        {to && <Link to={to} aria-label="Open it" className="shrink-0 text-muted-foreground/60 hover:text-foreground"><ExternalLink className="h-3 w-3" /></Link>}
      </span>
    );
  };

  return (
    <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l border-border bg-background/95 shadow-xl backdrop-blur lg:w-96 lg:max-w-none lg:shadow-none">
      <header className="flex items-center gap-2 border-b border-border px-4 py-3">
        <BrainCircuit className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground">ZAC's reasoning</h2>
        <button onClick={close} aria-label="Close the reasoning panel" className="ml-auto rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <section className="border-b border-border px-4 py-3">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Now</h3>
          {active.length === 0 ? (
            <p className="mt-1.5 text-xs text-muted-foreground">Nothing under way.</p>
          ) : (
            <ul className="mt-1.5 space-y-2.5">
              {active.map((a) => (
                <li key={`${a.kind}:${a.id}`} className="text-sm font-medium">
                  <Name s={{ kind: a.kind, id: a.id, title: a.title }} />
                  <Strip stage={a.stage} />
                  {a.line && <p className="mt-1 text-xs font-normal text-muted-foreground">{a.line}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>

        {drawn && (
          <section className="border-b border-border px-4 py-3">
            <h3 className="mb-2 flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Pipeline · <span className="truncate normal-case tracking-normal"><Name s={drawn} /></span>
            </h3>
            <PipelineDiagram key={`${drawn.kind}:${drawn.id}`} kind={drawn.kind} id={drawn.id} />
          </section>
        )}

        <section className="px-4 py-3">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Log</h3>
          {entries.length === 0 ? (
            <p className="mt-1.5 text-xs text-muted-foreground">No lines in the last day.</p>
          ) : (
            <ol className="mt-1.5 space-y-1.5">
              {entries.map((e) => (
                <li key={e.id} className="text-xs leading-snug">
                  <div className="flex items-baseline gap-1.5">
                    <span className="shrink-0 font-mono text-[10px] text-muted-foreground/70">{timeOf(e.at)}</span>
                    <span className={cn('shrink-0 text-[10px] uppercase', STAGE_TONE[e.stage])}>{e.stage}</span>
                    {e.ref_id !== null && <Name s={{ kind: e.kind, id: e.ref_id, title: e.title }} />}
                  </div>
                  <p className="break-words text-foreground/90 [overflow-wrap:anywhere]">{e.line}</p>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </aside>
  );
}
